import type { QueryRequest, TableExpressionFilterDefinition } from '@cognite/sdk';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';
import { CDF_MAX_PAGE_SIZE, type DataModelView } from '#core/lib/cognite/view.ts';

interface QueryProps {
  filter?: TableExpressionFilterDefinition[];
  limit?: number;
  /**
   * Forwarded to the underlying suspense query. Set `staleTime: Infinity` for
   * reference data that should load once and never re-suspend while mounted.
   */
  staleTime?: number;
  gcTime?: number;
}

export const useViewInstances = <T extends DataModelView>(
  source: T,
  { filter = [], limit = CDF_MAX_PAGE_SIZE, staleTime, gcTime }: QueryProps = {},
) => {
  const client = useCogniteClient();

  const query: QueryRequest = {
    with: {
      [source.externalId]: {
        nodes: {
          filter: {
            and: [{ hasData: [source] }, ...filter],
          },
        },
        limit,
      },
    },
    select: {
      [source.externalId]: {
        sources: [{ source, properties: ['*'] }],
      },
    },
  };

  return useSuspenseQuery({
    queryKey: ['fusionInstanceQuery', { data: { query } }] as const,
    queryFn: async ({ queryKey: [, { data }] }) => client?.instances.query(data.query),
    select: (data) => data?.items[source.externalId]?.map(source.parseNode).filter((it) => it !== undefined),
    staleTime,
    gcTime,
  });
};
